import { bookablePackages } from './bookingPackages';

/**
 * How many ITEMS the whole booking holds — the booker's own cart PLUS every
 * other person's service legs. The collapsed summary bar's label is built from
 * this, so a booking whose guests carry most of the services no longer reads as
 * the booker's single service name.
 *
 * Both footers (BookingBox on the service step, SelectTime on the time step)
 * used to compute this independently from the booker's cart alone, so a
 * "you: 1 service / partner: 2 services" booking announced itself as one
 * truncated service name. This is the single source of that count.
 *
 * `participants[0]` is the booker and is DELIBERATELY skipped: their items are
 * passed in as full objects via bookerServices/bookerPackages (participants[0]
 * carries the same legs id-only, so counting both would double it).
 *
 * @param {Array} bookerServices
 * @param {Array} bookerPackages
 * @param {Array} participants - index 0 = booker (skipped), 1+ = guests/partner
 * @returns {number}
 */
export function countBookingItems({ bookerServices = [], bookerPackages = [], participants = [] } = {}) {
  let count = (bookerServices?.length ?? 0) + bookablePackages(bookerPackages).length;
  for (let i = 1; i < (participants?.length ?? 0); i += 1) {
    count += participants[i]?.services?.length ?? 0;
  }
  return count;
}

/**
 * The ONE item to name in the collapsed summary bar, or null when the bar
 * should show a count instead ("3 services").
 *
 * Naming an item is only honest when the WHOLE booking is that item — if a
 * partner or guest carries anything, the booker's single service is not what
 * the customer is buying. Returns null in that case, and also when the sole
 * item belongs to someone other than the booker (the bar has no display name
 * for a guest's leg — guests hold id-only legs, not service objects).
 *
 * @returns {object|null} the booker service/package to name, or null
 */
export function soleSummaryItem({ bookerServices = [], bookerPackages = [], participants = [] } = {}) {
  if (countBookingItems({ bookerServices, bookerPackages, participants }) !== 1) return null;
  const bookerItems = [...bookerServices, ...bookablePackages(bookerPackages)];
  return bookerItems.length === 1 ? bookerItems[0] : null;
}

/**
 * The cart a CAMPAIGN is measured against: the BOOKER's own legs.
 *
 * Not the same list as the legs the booking is CHARGED for — that is every
 * person's, and always has been (`computeBookingTotals`, bookingLegs.js). A
 * promotion is a different kind of thing from a bill: it is something the
 * person who booked qualified for. This briefly widened to the whole party,
 * on the reasoning that the server charges the whole party; the owner
 * overruled it, because measuring the party turns "spend ₪500, get 20% off"
 * into a discount you reach by bringing friends.
 *
 * The signature is why this file has a function for something so small: it
 * takes the BOOKER's ids and nothing else, so a guest's leg has no door into
 * the cart and the rule cannot drift back one call site at a time. The server
 * measures the same way (appointments.service.ts createMultiCustomer,
 * biz-bookings.controller.ts) so the number quoted here is the number charged.
 *
 * Duplicates within the booker's own cart are kept: the campaign engine prices
 * each occurrence of a service id (campaigns.service.ts iterates `serviceIds`,
 * not a Set), so two of the same service in one cart discount twice.
 *
 * Staff at the desk may choose to apply a campaign the booker's cart did not
 * unlock (portal `staffApplyCampaign`). That is a portal affordance and a
 * server decision; a customer's own browser has no such lever, which is why
 * there is no flag here.
 *
 * @param {Array<string>} bookerServiceIds
 * @returns {Array<string>}
 */
export function campaignCartServiceIds(bookerServiceIds = []) {
  return (Array.isArray(bookerServiceIds) ? bookerServiceIds : []).filter(Boolean);
}

/**
 * What the BOOKER spends, for a campaign's minimum-spend check.
 *
 * Two things separate this from `computeBookingTotals().subtotal`:
 *
 *   1. It counts the booker's legs only, matching `campaignCartServiceIds`
 *      above and the server's own measurement — so "minimum met" on screen and
 *      "campaign applied" on the bill agree. Guests' legs are still charged;
 *      they are simply not what eligibility is measured on.
 *   2. It counts hidden-price services at their real price. The display
 *      subtotal zeroes them (the business chose not to publish the number),
 *      but the money still lands on the bill, so it still counts toward a
 *      minimum. That split is the pre-existing rule and is preserved.
 *
 * `participants`/`priceById` are accepted and ignored so the call sites read
 * the same as their neighbours; nothing about a guest can change this number.
 *
 * @param {Object} params
 * @param {Array} params.bookerServices - booker cart items carrying `.price`
 * @param {Array} params.bookerPackages - booker packages carrying `.price`
 * @returns {number}
 */
export function campaignEligibilitySubtotal({
  bookerServices = [],
  bookerPackages = [],
} = {}) {
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  let sum = 0;
  for (const item of [...(bookerServices ?? []), ...(bookerPackages ?? [])]) {
    sum += num(item?.price);
  }
  return sum;
}

/**
 * The REAL span of a chosen slot in whole minutes, from the `startsAt`/`endsAt`
 * the server returned for it. For a booking with more than one person the
 * slots oracle sets `endsAt = startsAt + makespanMin`, which accounts for
 * SEQUENTIAL rounds when there are more people than specialists (6 people
 * through 2 chairs = 3 rounds) — always >= the parallel estimate.
 *
 * Returns null when either bound is missing/invalid or the span is
 * non-positive, so callers fall back to their estimate.
 */
export function computeMakespanMinutes(startsAt, endsAt) {
  if (startsAt == null || endsAt == null) return null;
  const start = new Date(startsAt).getTime();
  const end = new Date(endsAt).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const minutes = Math.round((end - start) / 60000);
  return minutes > 0 ? minutes : null;
}

/**
 * The duration to SHOW the customer — the single rule, shared by the time step
 * and the review step so the two can never disagree.
 *
 * `parallelMin` is `computeBookingTotals(...).durationMin`: the sum for one
 * person, the longest single person's chain when several people run in
 * parallel. Once a slot is chosen, a multi-person booking has something better:
 * the server's own span for it, which also covers sequential rounds. Take it
 * whenever it is at least the parallel estimate (a shorter span means a stale
 * `endsAt`, not a real chosen slot).
 *
 * A one-person booking keeps the estimate: its slot `endsAt` can include the
 * business's pre/post buffer, which is not time the customer is in the chair.
 *
 * @param {Object} p
 * @param {number} p.parallelMin
 * @param {number} [p.participantCount=0] - people on the booking (0/1 = just the booker)
 * @param {string} [p.startsAt]
 * @param {string} [p.endsAt]
 * @returns {number}
 */
export function resolveShownDuration({ parallelMin, participantCount = 0, startsAt, endsAt }) {
  if (participantCount <= 1) return parallelMin;
  const span = computeMakespanMinutes(startsAt, endsAt);
  return span != null && span >= parallelMin ? span : parallelMin;
}

/**
 * Computes booking totals, duration, and per-person breakdown.
 * Pure function—no React, no app imports.
 *
 * @param {Object} params
 * @param {Array} params.bookerServices - Booker's services (carry `.price`, `.durationMin`, `.hidePrice?`, `.priceType?`)
 * @param {Array} params.bookerPackages - Booker's packages (same structure as services)
 * @param {Array} params.participants - Participant legs; index 0 is booker, index 1+ are guests/partners. Each has `.services` array with `{ serviceId, specialistId? }`
 * @param {Map} params.priceById - Map<serviceId, { price, durationMin, hidePrice?, priceType? }>
 * @param {number} [params.discount=0] - Discount amount
 * @param {boolean} [params.coupled=false] - True if booking is coupled (parallel duration calc)
 *
 * @returns {Object}
 * @returns {number} subtotal - Sum of booker + all participants. Only `hidePrice` items are zeroed;
 *   a `priceType==='from'` item CONTRIBUTES its price (the shown Total is a "Starting from" estimate;
 *   the exact final price is settled at appointment completion).
 * @returns {number} bookerSubtotal - The numeric booker-only contribution (same skip rule as `subtotal`);
 *   adopters derive the participants' extra as `subtotal - bookerSubtotal` without a divergent parallel sum.
 * @returns {number} discount - Echo of input discount
 * @returns {number} total - max(0, subtotal - discount)
 * @returns {number} durationMin - MAX single-person duration if coupled OR multi-person; else SUM
 * @returns {boolean} hasFromPrice - True if any counted item is priceType==='from'
 * @returns {boolean} hasHiddenPrice - True if ANY item on the booking (booker's cart OR
 *   anyone else's leg) hides its price. Lives here, with the money math, because the
 *   flag and the number must agree: this util ZEROES a hidden leg, so any screen that
 *   shows the number owes the customer the same "price on request" disclosure. Each
 *   step used to re-derive it from the booker's cart alone and dropped a guest's
 *   hidden leg.
 * @returns {Array} perPerson - Array of { label: 'booker'|number, services: Array<{ serviceId, price, durationMin, specialistId? }> }
 */
export function computeBookingTotals({
  bookerServices = [],
  bookerPackages = [],
  participants = [],
  priceById = new Map(),
  discount = 0,
  coupled = false,
}) {
  let subtotal = 0;
  let hasFromPrice = false;
  let hasHiddenPrice = false;

  // Helper to compute price and duration, accounting for skip rules.
  // Only `hidePrice` zeroes the numeric price; a `priceType==='from'` item
  // CONTRIBUTES its price (shown as a "Starting from" estimate) but still flags
  // hasFromPrice so the UI can prefix "Starting from".
  const processItem = (item) => {
    const skipNumeric = item.hidePrice;
    const price = skipNumeric ? 0 : (item.price ?? 0);
    const duration = item.durationMin ?? 0;
    if (item.priceType === 'from') {
      hasFromPrice = true;
    }
    if (skipNumeric) {
      hasHiddenPrice = true;
    }
    return { price, duration };
  };

  // Booker items. The package the booking can carry, not every one the cart
  // holds: a second package was summed here and never booked (SWP-P-14).
  const bookerItems = [...bookerServices, ...bookablePackages(bookerPackages)];
  const bookerProcessed = bookerItems.map(processItem);
  const bookerPrice = bookerProcessed.reduce((sum, item) => sum + item.price, 0);
  const bookerDuration = bookerProcessed.reduce((sum, item) => sum + item.duration, 0);
  subtotal += bookerPrice;

  // Per-person breakdown: start with booker
  const perPerson = [
    {
      label: 'booker',
      services: bookerItems.map((item) => ({
        serviceId: item._id,
        price: item.price,
        durationMin: item.durationMin,
        specialistId: item.specialistId,
      })),
    },
  ];

  // Array to track per-person durations for MAX calculation
  const perPersonDurations = [bookerDuration];

  // Process participants (guests/partners)
  for (let i = 1; i < participants.length; i++) {
    const participant = participants[i];
    let personPrice = 0;
    let personDuration = 0;

    const personServices = [];

    if (participant.services) {
      for (const leg of participant.services) {
        const metadata = priceById.get(leg.serviceId);
        if (metadata) {
          const skipNumeric = metadata.hidePrice;
          const price = skipNumeric ? 0 : metadata.price;
          const duration = metadata.durationMin;
          if (skipNumeric) {
            hasHiddenPrice = true;
          }

          personPrice += price;
          personDuration += duration;

          if (metadata.priceType === 'from') {
            hasFromPrice = true;
          }

          personServices.push({
            serviceId: leg.serviceId,
            price: metadata.price,
            durationMin: duration,
            specialistId: leg.specialistId,
          });
        }
      }
    }

    subtotal += personPrice;
    perPersonDurations.push(personDuration);
    perPerson.push({
      label: i,
      services: personServices,
    });
  }

  // Duration calculation: MAX if coupled or multi-person, else SUM
  let durationMin;
  const isMultiPerson = participants.length > 1;
  if (coupled || isMultiPerson) {
    // MAX of all per-person durations
    durationMin = Math.max(...perPersonDurations, 0);
  } else {
    // Single cart: SUM
    durationMin = perPersonDurations.reduce((sum, d) => sum + d, 0);
  }

  const total = Math.max(0, subtotal - discount);

  return {
    subtotal,
    bookerSubtotal: bookerPrice,
    discount,
    total,
    durationMin,
    hasFromPrice,
    hasHiddenPrice,
    perPerson,
  };
}
